import { useCallback, useEffect, useRef, useState } from 'react';
import { analyzeRegression, analyzeTest } from '../../../services/aiService';

export const AI_ANALYSIS_TYPES = {
  FLOW: 'flow',
  REGRESSION: 'regression',
};

const getBackendMessage = (err) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  'Failed to fetch AI analysis';

const runAnalysisByType = (analysisType, targetId) => {
  if (!targetId) {
    throw new Error('Missing AI analysis target id');
  }

  if (analysisType === AI_ANALYSIS_TYPES.FLOW) {
    return analyzeTest(targetId);
  }

  if (analysisType === AI_ANALYSIS_TYPES.REGRESSION) {
    return analyzeRegression(targetId);
  }

  throw new Error(`Unsupported AI analysis type: ${analysisType}`);
};

export default function useAIAnalysis(analysisType, targetId) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, []);

  const runAnalysis = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setOpen(true);
    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await runAnalysisByType(analysisType, targetId);
      if (mountedRef.current && requestIdRef.current === requestId) {
        setData(response);
      }
    } catch (err) {
      console.error('AI Analysis Error:', err);
      if (mountedRef.current && requestIdRef.current === requestId) {
        setError(getBackendMessage(err));
      }
    } finally {
      if (mountedRef.current && requestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [analysisType, targetId]);

  return {
    open,
    setOpen,
    loading,
    data,
    error,
    runAnalysis,
  };
}
